import { T, N } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { Loader2, Download, FileText, CheckCircle2, Clock, Users, Instagram, Music, Eye, MousePointerClick, Flag } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { RefreshCw, Gift } from "lucide-react";
import { getCampaignReport, contactsDownloadUrl, reportPdfUrl, renewCampaign, invoicePdfUrl } from "@/lib/api";
import { TermsCheck, DiscountField, discountOf, fmt } from "@/components/campaign/CheckoutExtras";

const Referenti = ({ c }) => {
  const refs = c.referenti || [];
  if (!refs.length) return c.person ? <div data-testid={`contact-person-${c.id}`} className="text-[11px] font-normal text-[#71717a]">{T("Referente")}: {c.person}</div> : null;
  return (
    <div data-testid={`contact-person-${c.id}`} className="mt-1 space-y-0.5">
      {refs.map((d) => (
        <div key={d.name} className="text-[11px] font-normal text-[#5b6168]">
          {T("Referente")}: <span className="text-[#2B3036]">{d.name}</span>{d.role && ` · ${d.role}`}
          {d.source_url && <a href={d.source_url} target="_blank" rel="noreferrer noopener" className="ml-1.5 text-[#E0262C] underline">{T("fonte")}</a>}
          {!d.verified && <span className="ml-1.5 text-[10px] font-mono uppercase text-[#92400e]">{T("da verificare")}</span>}
        </div>
      ))}
    </div>
  );
};

const METRICS = [
  { k: "contacts", label: N("Contatti"), icon: Users },
  { k: "ig", label: N("Account Instagram"), icon: Instagram },
  { k: "tiktok", label: N("Account TikTok"), icon: Music },
  { k: "views", label: N("Visualizzazioni sito"), icon: Eye },
  { k: "clicks", label: N("Click sul sito"), icon: MousePointerClick },
];
const STATUS = {
  awaiting_start: [N("In preparazione"), "bg-[#dbeafe] text-[#1e40af]"],
  running: [N("In corso"), "bg-[#dcfce7] text-[#166534]"],
  paused: [N("In pausa"), "bg-[#fef3c7] text-[#92400e]"],
  completed: [N("Completata"), "bg-[#09090b] text-white"],
  refunded: [N("Rimborsata"), "bg-[#fee2e2] text-[#991b1b]"],
  pending_payment: [N("In attesa di pagamento"), "bg-[#f4f4f5] text-[#52525b]"],
};
const COLS = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4", 5: "md:grid-cols-5" };
const d = (iso) => (iso ? new Date(iso).toLocaleDateString(document.documentElement.lang || "it", { day: "numeric", month: "long", year: "numeric" }) : "—");

const Header = ({ r }) => {
  const [label, cls] = STATUS[r.status] || [r.status, ""];
  return (
    <div className="border border-[#e4e4e7] bg-white p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <div className="flex items-center gap-3 mb-3">
          <span data-testid="client-campaign-status" className={`text-[10px] font-mono uppercase px-2 py-1 ${cls}`}>{T(label)}</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#71717a]">{T("Report campagna")}</span>
          {r.renewed_from && <span data-testid="client-renewal-badge" className="text-[10px] font-mono uppercase border border-[#E0262C] text-[#E0262C] px-2 py-0.5">{T("Rinnovo")}</span>}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl leading-tight">{r.params?.skill} <span className="text-[#a1a1aa]">· {r.params?.area}</span></h1>
        <div className="font-mono text-xs text-[#71717a] mt-2 break-all">{r.landing_url}</div>
        {r.invoice_number && (
          <a data-testid="download-invoice-pdf" href={invoicePdfUrl(r.id)}
            className="mt-4 inline-flex items-center gap-1.5 border border-[#2B3036] hover:bg-[#2B3036] hover:text-white px-3 py-2 text-xs font-medium transition-colors">
            <FileText className="w-3.5 h-3.5" /> {T("Fattura")} {r.invoice_number} (PDF)
          </a>
        )}
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-3 border border-[#e4e4e7] divide-x divide-[#e4e4e7] text-center">
          <div className="p-3"><div className="text-[9px] uppercase font-mono text-[#71717a]">{T("Inizio")}</div><div className="text-xs font-mono mt-1">{r.start_date ? new Date(r.start_date).toLocaleDateString(document.documentElement.lang || "it") : "—"}</div></div>
          <div className="p-3"><div className="text-[9px] uppercase font-mono text-[#71717a]">{T("Fine")}</div><div className="text-xs font-mono mt-1">{r.end_date ? new Date(r.end_date).toLocaleDateString(document.documentElement.lang || "it") : "—"}</div></div>
          <div className="p-3"><div className="text-[9px] uppercase font-mono text-[#71717a]">{T("Giorni rim.")}</div><div data-testid="client-days-remaining" className="text-xs font-mono mt-1">{r.start_date ? r.days_remaining : r.duration_days}</div></div>
        </div>
        <div className="h-2 bg-[#f4f4f5]"><div data-testid="client-progress" className="h-full aurora-gradient transition-[width] duration-700" style={{ width: `${r.progress}%` }} /></div>
        <div className="text-[10px] font-mono text-[#71717a]">{T("Giorno")} {r.current_day || 0} di {r.duration_days} · {r.progress}%</div>
      </div>
    </div>
  );
};

const Counters = ({ r, title, values, testid }) => {
  const shown = METRICS.filter((m) => r.visible?.[m.k] || r.status === "completed");
  if (!shown.length) return null;
  return (
    <div data-testid={testid}>
      {title && <div className="text-[10px] uppercase tracking-widest font-mono text-[#71717a] mb-3">{title}</div>}
      <div className={`grid grid-cols-2 ${COLS[shown.length]} border border-[#e4e4e7] divide-x divide-y md:divide-y-0 divide-[#e4e4e7]`}>
        {shown.map((m) => (
          <div key={m.k} className="p-6">
            <m.icon className="w-4 h-4 text-[#E0262C] mb-4" />
            <div data-testid={`${testid}-${m.k}`} className="font-display text-4xl leading-none">{(values?.[m.k] ?? 0).toLocaleString("it-IT")}</div>
            <div className="text-[11px] text-[#71717a] mt-2">{T(m.label)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DailyTable = ({ r }) => {
  const cols = METRICS.filter((m) => r.visible?.[m.k]);
  if (!r.days.length) return null;
  const count = (day, k) => day.counts?.[k] ?? (["views", "clicks"].includes(k) ? day[k] : day[k]?.length);
  const max = Object.fromEntries(cols.map((m) => [m.k, Math.max(1, ...r.days.map((x) => count(x, m.k) || 0))]));
  return (
    <div data-testid="client-daily-table" className="border border-[#e4e4e7] bg-white overflow-x-auto">
      <div className="px-5 py-3 border-b border-[#e4e4e7] font-display text-lg">{T("Andamento giorno per giorno")}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-[#71717a] font-mono bg-[#fafafa]">
            <th className="px-5 py-3">{T("Giorno")}</th>
            {cols.map((m) => <th key={m.k} className="px-5 py-3">{T(m.label)}</th>)}
          </tr>
        </thead>
        <tbody>
          {r.days.map((day) => (
            <tr key={day.day} data-testid={`client-day-${day.day}`} className="border-t border-[#e4e4e7]">
              <td className="px-5 py-3"><span className="font-display text-lg mr-2">{day.day}</span><span className="text-[11px] font-mono text-[#71717a]">{new Date(day.date).toLocaleDateString(document.documentElement.lang || "it")}</span></td>
              {cols.map((m) => {
                const v = count(day, m.k);
                return (
                  <td key={m.k} className="px-5 py-3">
                    {v === undefined ? <span className="text-[#d4d4d8]">—</span> : (
                      <div className="flex items-center gap-2">
                        <span className="font-mono w-10">{v}</span>
                        <div className="h-1.5 bg-[#f4f4f5] flex-1 min-w-[40px]"><div className="h-full aurora-gradient" style={{ width: `${(v / max[m.k]) * 100}%` }} /></div>
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Downloads = ({ id }) => (
  <div className="flex gap-2">
    <a data-testid="download-csv" href={contactsDownloadUrl(id, "csv")} className="inline-flex items-center gap-1.5 border border-[#e4e4e7] hover:border-[#09090b] px-3 py-2 text-xs font-medium"><Download className="w-3.5 h-3.5" /> CSV</a>
    <a data-testid="download-pdf" href={contactsDownloadUrl(id, "pdf")} className="inline-flex items-center gap-1.5 bg-[#09090b] text-white hover:bg-[#E0262C] px-3 py-2 text-xs font-medium transition-colors"><FileText className="w-3.5 h-3.5" /> PDF</a>
  </div>
);

const ContactsList = ({ r }) => {
  const contacts = r.days.flatMap((x) => (x.contacts || []).map((c) => ({ ...c, day: x.day })));
  if (!r.visible?.contacts) return null;
  return (
    <div data-testid="client-contacts" className="border border-[#e4e4e7] bg-white">
      <div className="px-5 py-3 border-b border-[#e4e4e7] flex items-center justify-between">
        <span className="font-display text-lg">{T("Contatti trovati")} <span className="text-[#a1a1aa]">({contacts.length})</span></span>
        <Downloads id={r.id} />
      </div>
      <div className="overflow-x-auto max-h-[480px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#fafafa]">
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#71717a] font-mono">
              {["Ragione sociale / Nome", "Indirizzo", "Email / Sito web", "Telefono", "Giorno"].map((h) => <th key={h} className="px-5 py-3">{T(h)}</th>)}
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} className="border-t border-[#e4e4e7]">
                <td className="px-5 py-3 font-medium">{c.name}<Referenti c={c} /></td>
                <td className="px-5 py-3 text-[#52525b]">{c.address}</td>
                <td className="px-5 py-3 font-mono text-xs">{c.email || (c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-[#E0262C]">{c.website}</a>)}</td>
                <td className="px-5 py-3 font-mono text-xs whitespace-nowrap">{c.phone}</td>
                <td className="px-5 py-3 font-mono text-xs text-[#71717a]">{c.day}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SocialList = ({ r, k, title, icon: Icon }) => {
  const items = r.days.flatMap((x) => x[k] || []);
  if (!r.visible?.[k]) return null;
  return (
    <div data-testid={`client-${k}`} className="border border-[#e4e4e7] bg-white">
      <div className="px-5 py-3 border-b border-[#e4e4e7] flex items-center gap-2 font-display text-lg"><Icon className="w-4 h-4" /> {title} <span className="text-[#a1a1aa]">({items.length})</span></div>
      <div className="p-4 flex flex-wrap gap-2 max-h-72 overflow-y-auto">
        {items.map((i) => (
          <a key={i.id} href={i.url} target="_blank" rel="noreferrer" className="border border-[#e4e4e7] bg-white hover:border-[#E0262C] px-3 py-1.5 text-sm font-mono transition-colors">{i.handle}</a>
        ))}
        {!items.length && <span className="text-xs text-[#a1a1aa] font-mono">{T("In arrivo...")}</span>}
      </div>
    </div>
  );
};

const LoyaltyBanner = ({ lc, usable }) => (
  <div data-testid="loyalty-code-box" className="border border-dashed border-[#E0262C] bg-[#E0262C]/10 p-4 flex flex-wrap items-center gap-3">
    <Gift className="w-5 h-5 text-[#E0262C]" />
    <div className="text-sm mr-auto">
      {usable ? <>{T("Il tuo codice personale")} <strong>-{lc.value}%</strong> {T("è già applicato al rinnovo. Valido fino al")} {d(lc.expires_at)}.</>
        : <>{T("Codice personale")} {lc.uses >= lc.max_uses ? "già utilizzato" : "scaduto"}.</>}
    </div>
    <button data-testid="loyalty-code-copy" onClick={() => { navigator.clipboard?.writeText(lc.code); toast.success(T("Codice copiato")); }}
      className="font-mono text-base bg-white text-[#2B3036] px-3 py-1.5 tracking-wider hover:bg-[#E0262C] hover:text-white transition-colors">{lc.code}</button>
  </div>
);

const RenewBox = ({ r }) => {
  const currency = "chf";
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const lc = r.loyalty_code;
  const lcUsable = lc && lc.active && lc.uses < lc.max_uses && new Date(lc.expires_at) > new Date();
  const [discount, setDiscount] = useState(lcUsable ? { code: lc.code, type: "percent", value: lc.value } : null);
  const gross = r.price_gross || r.price;
  const total = gross - discountOf(discount, gross);
  if (r.renewal) {
    return (
      <div data-testid="renewal-exists" className="border border-[#cfd6d9] bg-white p-5 flex items-center gap-3 text-sm">
        <RefreshCw className="w-4 h-4 text-[#E0262C]" /> Hai già rinnovato questa campagna.
        <Link to={`/campaign/${r.renewal.id}`} className="ml-auto font-medium text-[#E0262C] hover:underline">{T("Vai al rinnovo →")}</Link>
      </div>
    );
  }
  const go = async () => {
    setBusy(true);
    try {
      const d = await renewCampaign(r.id, currency, terms, discount?.code || null);
      toast.info(T("Redirect a Stripe per il pagamento..."));
      window.location.href = d.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || T("Errore rinnovo")); setBusy(false); }
  };
  return (
    <div data-testid="renew-box" className="border border-[#2B3036] bg-[#2B3036] text-white p-6 space-y-4">
    <div className="flex flex-col md:flex-row md:items-center gap-4">
      <div className="mr-auto">
        <div className="font-display text-xl">{T("Vuoi altri")} {r.duration_days} {T("giorni di risultati?")}</div>
        <div className="text-sm text-[#c4c9ce]">Stessi parametri ({r.params?.skill} · {r.params?.area}), nuovi contatti e profili. Un solo pagamento.</div>
      </div>
      <button data-testid="renew-btn" onClick={go} disabled={busy || !terms}
        className="aurora-gradient text-white px-6 py-3 text-sm font-medium inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        Rinnova per altri {r.duration_days} {T("giorni ·")} {currency === "chf" ? "CHF" : "€"} {fmt(total)}
      </button>
    </div>
    {lc && <LoyaltyBanner lc={lc} usable={lcUsable} />}
    <div className="max-w-md text-[#2B3036]"><DiscountField gross={gross} discount={discount} setDiscount={setDiscount} /></div>
    <TermsCheck checked={terms} onChange={setTerms} dark />
    </div>
  );
};

export const ManagedCampaignReport = ({ id }) => {
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    const load = () => getCampaignReport(id).then(setR).catch((e) => setErr(e?.response?.data?.detail || T("Errore caricamento")));
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [id]);

  if (err) return <div className="border border-[#e4e4e7] bg-white p-10 text-center text-sm text-[#ef4444]">{err}</div>;
  if (!r) return <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#E0262C]" /></div>;
  const nothingYet = !Object.values(r.visible || {}).some(Boolean);

  return (
    <div data-testid="managed-report" className="space-y-6">
      {r.status === "completed" && (
        <div data-testid="completed-banner" className="bg-[#09090b] text-white p-6 flex flex-col md:flex-row md:items-center gap-4">
          <Flag className="w-6 h-6 text-[#10b981]" />
          <div className="mr-auto">
            <div className="font-display text-2xl">{T("Campagna terminata il")} {d(r.completed_at)}</div>
            <div className="text-sm text-[#a1a1aa]">{T("Ecco il riepilogo finale dei risultati della tua campagna.")}</div>
          </div>
          <a data-testid="download-report-pdf" href={reportPdfUrl(r.id)}
            className="inline-flex items-center gap-1.5 bg-white text-[#2B3036] hover:bg-[#E0262C] hover:text-white px-4 py-2.5 text-sm font-medium transition-colors">
            <FileText className="w-4 h-4" /> Report PDF
          </a>
        </div>
      )}
      {r.status === "completed" && <RenewBox r={r} />}
      <Header r={r} />
      {r.status === "awaiting_start" && (
        <div data-testid="awaiting-start-box" className="border border-[#bfdbfe] bg-[#eff6ff] p-6 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-[#2563eb] mt-0.5" />
          <div><div className="font-medium">{T("Pagamento ricevuto")}</div>
            <p className="text-sm text-[#52525b]">{T("Il nostro team sta preparando la tua campagna. Riceverai una notifica appena viene avviata.")}</p></div>
        </div>
      )}
      {r.status === "completed"
        ? <Counters r={r} title={T("Riepilogo finale")} values={r.summary} testid="client-summary" />
        : <Counters r={r} values={r.totals} testid="client-counters" />}
      {nothingYet && ["running", "paused"].includes(r.status) && (
        <div data-testid="no-data-yet" className="border border-dashed border-[#e4e4e7] p-10 text-center">
          <Clock className="w-5 h-5 mx-auto text-[#a1a1aa] mb-2" />
          <div className="text-sm text-[#52525b]">{T("La campagna è in corso: i primi risultati appariranno qui nelle prossime ore.")}</div>
        </div>
      )}
      <DailyTable r={r} />
      <ContactsList r={r} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SocialList r={r} k="ig" title={T("Account Instagram")} icon={Instagram} />
        <SocialList r={r} k="tiktok" title={T("Account TikTok")} icon={Music} />
      </div>
    </div>
  );
};
